import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BrandSpinner from "@/components/ui/BrandSpinner";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { getCanonicalUrl, SITE_NAME } from "@/config/site";
import { DemoGenieTokenService } from "@/services/demo/DemoGenieTokenService";
import LiveAgentPage from "./LiveAgentPage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

/**
 * Not found / expired state for a private demo link. Deliberately does not
 * reveal whether the token never existed or simply expired — a stranger
 * probing the URL space learns nothing either way.
 */
const DemoUnavailable = ({ companyName }: { companyName: string | null }) => (
  <>
    <Helmet>
      <title>Demo Unavailable | {SITE_NAME}</title>
      <meta name="robots" content="noindex,nofollow" />
      <link rel="canonical" href={getCanonicalUrl("/demo")} />
    </Helmet>
    <div
      className="min-h-dvh flex flex-col items-center justify-center px-4 py-12 text-center"
      style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)` }}
    >
      <div className="mb-6">
        <HelpGenieLogo size={56} />
      </div>
      <div
        className="mb-6 p-4 rounded-2xl"
        style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, ${SPARK_BLUE}15)` }}
      >
        <GenieIcon
          name="lock"
          size={40}
          color={MAGENTA_BEAM}
          secondaryColor={SPARK_BLUE}
          gradientEnabled
          label="Demo unavailable"
        />
      </div>
      <h1
        className="text-3xl md:text-4xl font-bold mb-3"
        style={{
          background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        This demo has ended
      </h1>
      <p className="text-gray-500 text-lg max-w-md mx-auto mb-8">
        {companyName
          ? `This private demo prepared for ${companyName} is no longer available.`
          : "This private demo link is no longer available."}{" "}
        If you'd like a new one, get in touch with your Help Genie contact.
      </p>
      <Link
        to="/contact"
        className="inline-flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold shadow-md hover:shadow-lg transition-shadow"
        style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})` }}
      >
        Contact Help Genie
      </Link>
    </div>
  </>
);

/**
 * Banner across the top of a private demo — every prospect demo carries this,
 * per the Demo Forge brand rule: unguessable URL, clearly labelled, never
 * mistaken for the prospect's own live service.
 */
const PrivateDemoBanner = ({ companyName }: { companyName: string }) => (
  <div
    className="w-full text-center px-4 py-2 text-sm font-medium text-white"
    style={{ background: `linear-gradient(90deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})` }}
  >
    Private demo prepared for {companyName} by Help Genie. Not affiliated with {companyName}.
  </div>
);

const PrivateDemoPage = () => {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ["demo-genie-token", token],
    queryFn: () => DemoGenieTokenService.resolve(token || ""),
    enabled: !!token,
    staleTime: 60 * 1000,
    retry: false,
  });

  const agentPageData = useMemo(() => {
    if (data?.status !== "ok") return null;
    return data.agent;
  }, [data]);

  if (!token || isLoading || !data) {
    return <BrandSpinner label="Opening your demo…" />;
  }

  if (data.status !== "ok") {
    return <DemoUnavailable companyName={data.status === "expired" ? data.companyName : null} />;
  }

  return (
    <>
      <Helmet>
        {/* Never indexed, never in the sitemap, never surfaced via Discover or
            the marketplace — this route exists only for whoever holds the link. */}
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <PrivateDemoBanner companyName={data.companyName} />
      <LiveAgentPage agentPageData={agentPageData} portalMode />
    </>
  );
};

export default PrivateDemoPage;
